import React, { useState, useEffect } from 'react';
import { View, Text, ScrollView, TouchableOpacity, RefreshControl, StyleSheet } from 'react-native';
import LoadingSpinner from './LoadingSpinner';

/**
 * Site Health Monitoring Tab
 * 
 * Shows:
 * - Overall system health status
 * - API endpoint health checks
 * - Database connectivity
 * - External service status
 * - Recent error rates
 */
const SiteHealthTab = () => {
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  
  const functionsUrl = __DEV__ 
    ? 'http://127.0.0.1:5001/bet-optimized-fe922/us-central1'
    : 'https://us-central1-bet-optimized-fe922.cloudfunctions.net';
  
  const fetchHealth = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const checks = {
        cacheStats: { status: 'unknown', latency: 0, error: null },
        firestoreRead: { status: 'unknown', latency: 0, error: null },
        nlOdds: { status: 'unknown', latency: 0, error: null },
      };
      
      // Test Cache Stats endpoint
      try {
        const start1 = Date.now();
        const res1 = await fetch(`${functionsUrl}/getCacheStats`);
        checks.cacheStats.latency = Date.now() - start1;
        checks.cacheStats.status = res1.ok ? 'healthy' : 'error';
        if (!res1.ok) checks.cacheStats.error = `HTTP ${res1.status}`;
      } catch (err) {
        checks.cacheStats.status = 'error';
        checks.cacheStats.error = err.message;
      }
      
      // Test NL Odds endpoint (with simple query)
      try {
        const start2 = Date.now();
        const res2 = await fetch(`${functionsUrl}/nlOdds`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ q: 'nba' })
        });
        checks.nlOdds.latency = Date.now() - start2;
        checks.nlOdds.status = res2.ok ? 'healthy' : 'error';
        if (!res2.ok) checks.nlOdds.error = `HTTP ${res2.status}`;
      } catch (err) {
        checks.nlOdds.status = 'error';
        checks.nlOdds.error = err.message;
      }
      
      // Calculate overall health
      const allHealthy = Object.values(checks).every(c => c.status === 'healthy');
      const anyError = Object.values(checks).some(c => c.status === 'error');
      
      setHealthData({
        overallStatus: allHealthy ? 'healthy' : (anyError ? 'degraded' : 'unknown'),
        lastCheck: new Date(),
        checks
      });
    } catch (err) {
      console.error('[SiteHealth] Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  useEffect(() => {
    fetchHealth();
    const interval = setInterval(fetchHealth, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);
  
  const onRefresh = () => {
    setRefreshing(true);
    fetchHealth();
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return '#10B981';
      case 'degraded': return '#F59E0B';
      case 'error': return '#EF4444';
      default: return '#6B7280';
    }
  };
  
  const getStatusText = (status) => {
    switch (status) {
      case 'healthy': return '✅ All Systems Operational';
      case 'degraded': return '⚠️ Degraded Performance';
      case 'error': return '🔴 System Issues Detected';
      default: return '❓ Unknown Status';
    }
  };
  
  if (loading && !healthData) {
    return (
      <View style={styles.container}>
        <View style={styles.centerContent}>
          <LoadingSpinner size={32} color="#8B5CF6" />
          <Text style={styles.loadingText}>Running health checks...</Text>
        </View>
      </View>
    );
  }
  
  if (error && !healthData) {
    return (
      <View style={styles.container}>
        <View style={styles.centerContent}>
          <Text style={styles.errorTitle}>❌ Health Check Failed</Text>
          <Text style={styles.errorMessage}>{error}</Text>
          <TouchableOpacity style={styles.retryButton} onPress={fetchHealth}>
            <Text style={styles.retryButtonText}>Retry</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }
  
  return (
    <ScrollView 
      style={styles.container}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor="#8B5CF6" />
      }
    >
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.title}>🏥 Site Health</Text>
        <Text style={styles.subtitle}>System status & diagnostics</Text>
      </View>
      
      {/* Overall Status Card */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>Overall Status</Text>
        <View style={[styles.statusBadge, { backgroundColor: getStatusColor(healthData?.overallStatus) + '20' }]}>
          <Text style={[styles.statusText, { color: getStatusColor(healthData?.overallStatus) }]}>
            {getStatusText(healthData?.overallStatus)}
          </Text>
        </View>
        <View style={styles.statRow}>
          <Text style={styles.statLabel}>Last Check:</Text>
          <Text style={styles.statValue}>{healthData?.lastCheck?.toLocaleTimeString()}</Text>
        </View>
      </View>
      
      {/* Service Checks */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>Service Health Checks</Text>
        
        {/* Cache Stats */}
        <View style={styles.serviceRow}>
          <View style={styles.serviceDot} style={{ backgroundColor: getStatusColor(healthData?.checks?.cacheStats?.status) }} />
          <View style={styles.serviceDetails}>
            <Text style={styles.serviceName}>Cache Stats API</Text>
            <Text style={styles.serviceLatency}>{healthData?.checks?.cacheStats?.latency}ms</Text>
            {healthData?.checks?.cacheStats?.error && (
              <Text style={styles.serviceError}>{healthData.checks.cacheStats.error}</Text>
            )}
          </View>
        </View>
        
        {/* NL Odds */}
        <View style={styles.serviceRow}>
          <View style={[styles.serviceDot, { backgroundColor: getStatusColor(healthData?.checks?.nlOdds?.status) }]} />
          <View style={styles.serviceDetails}>
            <Text style={styles.serviceName}>Natural Language Query API</Text>
            <Text style={styles.serviceLatency}>{healthData?.checks?.nlOdds?.latency}ms</Text>
            {healthData?.checks?.nlOdds?.error && (
              <Text style={styles.serviceError}>{healthData.checks.nlOdds.error}</Text>
            )}
          </View>
        </View>
      </View>
      
      {/* Quick Actions */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>Quick Actions</Text>
        <TouchableOpacity style={styles.actionButton} onPress={fetchHealth}>
          <Text style={styles.actionButtonText}>🔄 Run Health Check</Text>
        </TouchableOpacity>
      </View>
      
      {/* System Info */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>System Information</Text>
        <View style={styles.statRow}>
          <Text style={styles.statLabel}>Environment:</Text>
          <Text style={styles.statValue}>{__DEV__ ? 'Development' : 'Production'}</Text>
        </View>
        <View style={styles.statRow}>
          <Text style={styles.statLabel}>Functions URL:</Text>
          <Text style={[styles.statValue, { fontSize: 11 }]} numberOfLines={1}>{functionsUrl}</Text>
        </View>
      </View>
      
      {/* Auto-refresh indicator */}
      <View style={styles.footer}>
        <Text style={styles.footerText}>Auto-refreshes every 60 seconds</Text>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0F172A',
  },
  centerContent: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  loadingText: {
    color: '#94A3B8',
    fontSize: 16,
    marginTop: 16,
  },
  errorTitle: {
    color: '#EF4444',
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 12,
  },
  errorMessage: {
    color: '#94A3B8',
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 20,
  },
  retryButton: {
    backgroundColor: '#8B5CF6',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  retryButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  header: {
    padding: 20,
    paddingBottom: 12,
  },
  title: {
    color: '#FFFFFF',
    fontSize: 28,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  subtitle: {
    color: '#94A3B8',
    fontSize: 14,
  },
  card: {
    backgroundColor: '#1E293B',
    marginHorizontal: 16,
    marginBottom: 16,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#334155',
  },
  cardTitle: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 12,
  },
  statusBadge: {
    paddingVertical: 16,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 12,
  },
  statusText: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  statRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  statLabel: {
    color: '#94A3B8',
    fontSize: 14,
  },
  statValue: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
  serviceRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 16,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#334155',
  },
  serviceDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: 12,
    marginTop: 4,
  },
  serviceDetails: {
    flex: 1,
  },
  serviceName: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '500',
    marginBottom: 4,
  },
  serviceLatency: {
    color: '#94A3B8',
    fontSize: 14,
    marginBottom: 4,
  },
  serviceError: {
    color: '#EF4444',
    fontSize: 12,
    fontStyle: 'italic',
  },
  actionButton: {
    backgroundColor: '#8B5CF6',
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: 'center',
  },
  actionButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  footer: {
    padding: 20,
    alignItems: 'center',
  },
  footerText: {
    color: '#64748B',
    fontSize: 12,
    fontStyle: 'italic',
  },
});

export default SiteHealthTab;

